
import React, { useState } from "react";
import { GearCategory, GearItem, categoryIcons } from "@/types/gear";
import { Input } from "../ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Textarea } from "../ui/textarea";
import { GearTable } from "./GearTable";
import { useCurrency } from "@/contexts/CurrencyContext";
import { formatCurrency } from "@/lib/utils";

interface GearDepartmentProps {
  title: string;
  items: GearItem[];
  categories: GearCategory[];
  notes: string;
  onNotesChange: (notes: string) => void;
  onAddItem: () => void;
  onEditItem: (item: GearItem) => void;
  onDeleteItem: (id: string) => void;
  canEdit: boolean;
  showBudget?: boolean;
  projectId: string;
  onAddFromInventory?: () => void;
  isPublicView?: boolean;
}

export function GearDepartment({
  title,
  items,
  categories,
  notes,
  onNotesChange,
  onAddItem,
  onEditItem,
  onDeleteItem,
  canEdit,
  showBudget = true,
  projectId,
  onAddFromInventory,
  isPublicView = false
}: GearDepartmentProps) {
  const { currency } = useCurrency();
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState(notes);

  // For public views, disable all editing capabilities
  const allowEdit = canEdit && !isPublicView;

  // Filter items by categories
  const departmentItems = items.filter((item) =>
    categories.includes(item.category as GearCategory)
  );

  // Calculate department total cost
  const departmentTotal = departmentItems.reduce((total, item) => {
    return total + (item.quantity || 0) * (item.cost_per_unit || 0) * (item.days_needed || 1);
  }, 0);

  const handleSaveNotes = () => {
    onNotesChange(editedNotes);
    setIsEditing(false);
    toast.success(`${title} notes updated`);
  };

  const handleCancelEdit = () => {
    setEditedNotes(notes);
    setIsEditing(false);
  };

  return (
    <div className="relative">
      <Card className="bg-card/30 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <CardTitle>{title}</CardTitle>
            {allowEdit && (
              <div className="flex gap-2">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={onAddItem}
                  className="h-8"
                >
                  <Plus className="mr-1 h-4 w-4" /> Add Item
                </Button>
                {onAddFromInventory && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={onAddFromInventory}
                    className="h-8 whitespace-nowrap"
                  >
                    <Package className="mr-1 h-4 w-4" /> From Inventory
                  </Button>
                )}
              </div>
            )}
          </div>
          <CardDescription>
            {showBudget && (
              <div className="flex items-center gap-2 text-sm">
                <span>Department Total:</span>
                <span className="font-semibold text-white">
                  {formatCurrency(departmentTotal, currency)}
                </span>
              </div>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isEditing ? (
            <div className="mb-4 relative">
              <div className="rounded-md bg-background/30 p-3 text-sm text-white/70 whitespace-pre-line min-h-20">
                {notes ? notes : "No notes added yet."}
              </div>
              {allowEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <div className="mb-4 space-y-2">
              <Textarea
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                placeholder={`Add notes for the ${title.toLowerCase()}...`}
                className="min-h-[100px]"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSaveNotes}>
                  Save Notes
                </Button>
              </div>
            </div>
          )}

          {/* Gear items table */}
          <GearTable
            items={departmentItems}
            onEdit={allowEdit ? onEditItem : undefined}
            onDelete={allowEdit ? onDeleteItem : undefined}
            canEdit={allowEdit}
            showBudget={showBudget}
            categories={categories}
            isPublicView={isPublicView}
          />

          {departmentItems.length === 0 && (
            <div className="text-center py-6 text-white/50">
              <p>No {title.toLowerCase()} items added yet.</p>
              {allowEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onAddItem}
                  className="mt-2"
                >
                  <Plus className="mr-1 h-4 w-4" /> Add Item
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
